// =============================================================================
// SimSense Platform — Friends Page (OT-246)
// =============================================================================

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";

type FriendUser = {
  id: string;
  displayName: string | null;
  profilePictureUrl: string | null;
  bio: string | null;
  location: string | null;
  tier: string | null;
  publicProfile: boolean;
};

type FriendRequest = {
  id: string;
  requesterId: string;
  addresseeId: string;
  status: string;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  requester: FriendUser | null;
  addressee: FriendUser | null;
};

type Tab = "friends" | "incoming" | "outgoing";

export function Friends() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("friends");
  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [incoming, setIncoming] = useState<FriendRequest[]>([]);
  const [outgoing, setOutgoing] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Send friend request state
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [friendsList, requestsData] = await Promise.all([
        api<FriendUser[]>("/api/friends/me"),
        api<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>("/api/friends/requests/me"),
      ]);
      setFriends(friendsList || []);
      setIncoming(requestsData?.incoming || []);
      setOutgoing(requestsData?.outgoing || []);
    } catch (err) {
      console.error("Failed to fetch friends data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Friends | SimSense";
    fetchData();
  }, [fetchData]);

  const handleAccept = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await api(`/api/friends/requests/${requestId}/accept`, { method: "POST" });
      toast.success("Friend request accepted!");
      await fetchData();
    } catch {
      toast.error("Failed to accept request.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await api(`/api/friends/requests/${requestId}/reject`, { method: "POST" });
      toast.success("Friend request declined.");
      await fetchData();
    } catch {
      toast.error("Failed to decline request.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await api(`/api/friends/requests/${requestId}`, { method: "DELETE" });
      toast.success("Friend request cancelled.");
      await fetchData();
    } catch {
      toast.error("Failed to cancel request.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendEmail.trim()) return;
    setSendLoading(true);
    try {
      // Search for user by email/display name, then send request
      const users = await api<FriendUser[]>(`/api/profiles/search?q=${encodeURIComponent(sendEmail.trim())}`);
      if (!users || users.length === 0) {
        toast.error("No user found with that name or email.");
        setSendLoading(false);
        return;
      }
      await api("/api/friends/requests", {
        method: "POST",
        body: JSON.stringify({ target_user_id: users[0].id }),
      });
      toast.success(`Friend request sent to ${users[0].displayName || "user"}!`);
      setSendEmail("");
      setShowSendForm(false);
      await fetchData();
    } catch (err: any) {
      const detail = err?.message || "Failed to send request.";
      toast.error(detail);
    } finally {
      setSendLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: "40px", textAlign: "center", color: "var(--text2)" }}>Loading friends...</div>;
  }

  const incomingCount = incoming.length;

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ margin: 0, fontSize: "18px" }}>Friends</h2>
        <button
          onClick={() => setShowSendForm(!showSendForm)}
          style={{
            padding: "7px 16px",
            fontSize: "12px",
            fontWeight: 600,
            color: "#fff",
            background: "var(--accent)",
            border: "none",
            borderRadius: "var(--radius-sm)",
            cursor: "pointer",
          }}
        >
          {showSendForm ? "Cancel" : "Add Friend"}
        </button>
      </div>

      {/* Send Request Form */}
      {showSendForm && (
        <form onSubmit={handleSendRequest} style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
          padding: "12px",
          background: "var(--card)",
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border)",
        }}>
          <input
            type="text"
            value={sendEmail}
            onChange={e => setSendEmail(e.target.value)}
            placeholder="Search by name or email..."
            style={{
              flex: 1,
              padding: "8px 12px",
              fontSize: "13px",
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              color: "var(--text)",
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={sendLoading || !sendEmail.trim()}
            style={{
              padding: "8px 16px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              background: sendLoading ? "var(--text3)" : "var(--accent)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: sendLoading ? "wait" : "pointer",
              opacity: !sendEmail.trim() ? 0.5 : 1,
            }}
          >
            {sendLoading ? "Sending..." : "Send"}
          </button>
        </form>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", borderBottom: "1px solid var(--border)", paddingBottom: "0" }}>
        {([
          { key: "friends" as Tab, label: "Friends", count: friends.length },
          { key: "incoming" as Tab, label: "Requests", count: incomingCount },
          { key: "outgoing" as Tab, label: "Sent", count: outgoing.length },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px",
              fontSize: "12px",
              fontWeight: 600,
              color: tab === t.key ? "var(--accent)" : "var(--text2)",
              background: "none",
              border: "none",
              borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
              cursor: "pointer",
              marginBottom: "-1px",
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                marginLeft: "6px",
                padding: "1px 6px",
                fontSize: "10px",
                fontWeight: 700,
                borderRadius: "8px",
                background: t.key === "incoming" && tab !== "incoming" ? "var(--accent)" : "rgba(255,255,255,0.1)",
                color: t.key === "incoming" && tab !== "incoming" ? "#fff" : "var(--text2)",
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "friends" && (
        friends.length === 0 ? (
          <EmptyState message="No friends yet. Send a friend request to get started!" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {friends.map(f => (
              <UserCard key={f.id} user={f} />
            ))}
          </div>
        )
      )}

      {tab === "incoming" && (
        incoming.length === 0 ? (
          <EmptyState message="No pending friend requests." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {incoming.map(req => (
              <RequestCard
                key={req.id}
                request={req}
                user={req.requester}
                type="incoming"
                actionLoading={actionLoading === req.id}
                onAccept={() => handleAccept(req.id)}
                onReject={() => handleReject(req.id)}
              />
            ))}
          </div>
        )
      )}

      {tab === "outgoing" && (
        outgoing.length === 0 ? (
          <EmptyState message="No sent friend requests." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {outgoing.map(req => (
              <RequestCard
                key={req.id}
                request={req}
                user={req.addressee}
                type="outgoing"
                actionLoading={actionLoading === req.id}
                onCancel={() => handleCancel(req.id)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function UserCard({ user }: { user: FriendUser }) {
  const initials = (user.displayName || "?")[0].toUpperCase();
  const tierColor = user.tier === "admin" ? "var(--bad)" : user.tier === "pro" ? "var(--accent)" : "var(--text3)";

  return (
    <Link
      to={`/profile/${user.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px",
        background: "var(--card)",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--border)",
        textDecoration: "none",
        color: "inherit",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--accent)")}
      onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
    >
      {user.profilePictureUrl ? (
        <img src={user.profilePictureUrl} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
      ) : (
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(59,130,246,0.15)", color: "var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", fontWeight: 700,
        }}>{initials}</div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: "13px" }}>
          {user.displayName || "Anonymous"}
          {user.tier && user.tier !== "free" && (
            <span style={{
              marginLeft: "8px", fontSize: "9px", fontWeight: 700,
              padding: "1px 6px", borderRadius: "4px", textTransform: "uppercase",
              background: `color-mix(in srgb, ${tierColor} 15%, transparent)`,
              color: tierColor,
            }}>{user.tier}</span>
          )}
        </div>
        {user.location && <div style={{ fontSize: "11px", color: "var(--text3)" }}>{user.location}</div>}
      </div>
    </Link>
  );
}

function RequestCard({ request, user, type, actionLoading, onAccept, onReject, onCancel }: {
  request: FriendRequest;
  user: FriendUser | null;
  type: "incoming" | "outgoing";
  actionLoading: boolean;
  onAccept?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
}) {
  if (!user) return null;
  const initials = (user.displayName || "?")[0].toUpperCase();
  const timeAgo = getTimeAgo(request.createdAt);

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px",
      background: "var(--card)",
      borderRadius: "var(--radius-sm)",
      border: "1px solid var(--border)",
    }}>
      {user.profilePictureUrl ? (
        <img src={user.profilePictureUrl} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
      ) : (
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(59,130,246,0.15)", color: "var(--accent)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", fontWeight: 700,
        }}>{initials}</div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: "13px" }}>{user.displayName || "Anonymous"}</div>
        <div style={{ fontSize: "11px", color: "var(--text3)" }}>{timeAgo}</div>
      </div>
      <div style={{ display: "flex", gap: "6px" }}>
        {type === "incoming" && (
          <>
            <button
              onClick={onAccept}
              disabled={actionLoading}
              style={{
                padding: "5px 12px", fontSize: "11px", fontWeight: 600,
                color: "#fff", background: "#10b981",
                border: "none", borderRadius: "var(--radius-sm)",
                cursor: actionLoading ? "wait" : "pointer",
                opacity: actionLoading ? 0.5 : 1,
              }}
            >Accept</button>
            <button
              onClick={onReject}
              disabled={actionLoading}
              style={{
                padding: "5px 12px", fontSize: "11px", fontWeight: 600,
                color: "var(--text2)", background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
                cursor: actionLoading ? "wait" : "pointer",
                opacity: actionLoading ? 0.5 : 1,
              }}
            >Decline</button>
          </>
        )}
        {type === "outgoing" && (
          <button
            onClick={onCancel}
            disabled={actionLoading}
            style={{
              padding: "5px 12px", fontSize: "11px", fontWeight: 600,
              color: "var(--text2)", background: "rgba(255,255,255,0.05)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              cursor: actionLoading ? "wait" : "pointer",
              opacity: actionLoading ? 0.5 : 1,
            }}
          >Cancel</button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{
      padding: "40px 20px",
      textAlign: "center",
      color: "var(--text3)",
      fontSize: "13px",
      background: "var(--card)",
      borderRadius: "var(--radius-sm)",
      border: "1px solid var(--border)",
    }}>
      {message}
    </div>
  );
}

function getTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
